'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { SNIPPET_TYPE } from '@repo/types';

export interface YtVideoCardAnalysisIntent {
  openSection?: 'key-claims' | 'comments';
  claimsTab?: SNIPPET_TYPE;
  openComments?: boolean;
}

interface YtVideoCardAnalysisIntentContextValue {
  intent: YtVideoCardAnalysisIntent | null;
  setIntent: (intent: YtVideoCardAnalysisIntent | null) => void;
  clearIntent: () => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const YtVideoCardAnalysisIntentContext = createContext<
  YtVideoCardAnalysisIntentContextValue | undefined
>(undefined);

export const YtVideoCardAnalysisIntentProvider: React.FC<
  PropsWithChildren<{ initialOpen?: boolean }>
> = ({ children, initialOpen = false }) => {
  const [intent, setIntentState] = useState<YtVideoCardAnalysisIntent | null>(
    null,
  );
  const [isOpen, setIsOpen] = useState(initialOpen);

  const setIntent = useCallback(
    (newIntent: YtVideoCardAnalysisIntent | null) => {
      setIntentState(newIntent);
      setIsOpen(true);
    },
    [],
  );

  const clearIntent = useCallback(() => {
    setIntentState(null);
  }, []);

  const value = useMemo<YtVideoCardAnalysisIntentContextValue>(
    () => ({
      intent,
      setIntent,
      clearIntent,
      isOpen,
      setIsOpen,
    }),
    [intent, setIntent, clearIntent, isOpen],
  );

  return (
    <YtVideoCardAnalysisIntentContext.Provider value={value}>
      {children}
    </YtVideoCardAnalysisIntentContext.Provider>
  );
};

export const useYtVideoCardAnalysis =
  (): YtVideoCardAnalysisIntentContextValue => {
    const context = useContext(YtVideoCardAnalysisIntentContext);
    if (!context) {
      throw new Error(
        'useYtVideoCardAnalysis must be used within YtVideoCardAnalysisIntentProvider',
      );
    }
    return context;
  };
